/**
 * Classify a Supabase Storage error into the three things a caller MUST tell
 * apart: the session is the problem, the object is definitively not there, or
 * the answer is inconclusive.
 *
 * The distinction is the whole point. Treating a transient failure as "missing"
 * is what stranded a real host for seven hours on 2026-09-07, and again on
 * 2026-09-21 (TAGO-MOBILE-4E): `vehicle-documents/.../tarjeta_*.jpg` existed,
 * its `owner_id` was the uploader, and `createSignedUrl` failed inside the
 * post-upload consistency window. The old check turned that into
 * `STORAGE_OBJECT_MISSING` and blocked the flow.
 *
 * Pure and dependency-free on purpose: the classification is the invariant, and
 * it must be unit-testable without a Supabase client.
 */

export type StorageErrorLike = {
  message: string
  code?: string
  status?: number
  statusCode?: string | number
  hint?: string | null
} | null

export type StorageErrorKind = 'auth-denied' | 'missing' | 'transient'

/**
 * Auth-denied shapes, in the words storage actually uses: RLS denial, gateway
 * 401/403, and expired tokens. Size and network failures must NOT match — they
 * need different advice and no session refresh.
 */
export function isAuthDeniedError(error: StorageErrorLike): boolean {
  if (!error) return false
  const status = Number(error.status ?? error.statusCode ?? 0)
  if (status === 401 || status === 403) return true
  const raw = error.message ?? ''
  return /row-level security|not authorized|unauthorized|permission denied|violates|forbidden|jwt.*(expired|invalid)|token.*(expired|invalid)|expired.*(jwt|token)/i.test(
    raw,
  )
}

/**
 * A definitive "this object is not in the bucket". Only an explicit 404 or an
 * explicit not-found message qualifies. Anything else — a timeout, a 5xx, a
 * DNS failure, a storage consistency gap — is inconclusive, never missing.
 */
export function isDefinitiveMissingError(error: StorageErrorLike): boolean {
  if (!error) return false
  const status = Number(error.status ?? error.statusCode ?? 0)
  if (status === 404) return true
  const raw = error.message ?? ''
  return /not found|does not exist|no such object|object not found/i.test(raw)
}

export function classifyStorageError(error: StorageErrorLike): StorageErrorKind {
  if (isAuthDeniedError(error)) return 'auth-denied'
  if (isDefinitiveMissingError(error)) return 'missing'
  return 'transient'
}
